import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Calculator, FlaskConical, GraduationCap, Globe, ChevronRight, ArrowRight, User, Users, UsersRound, Target, CalendarDays, Quote } from "lucide-react";
import { useI18n } from "../../lib/i18n";
import { api, photoUrl } from "../../lib/api";
import { LessonCard } from "../site/LessonCard";
import { PhotoPlaceholder, Avatar } from "../site/Placeholder";

const LEVELS = [
  { name: "Basisschool", icon: BookOpen, bg: "bg-tile-yellow", fg: "text-osk-orange" },
  { name: "EPB", icon: Calculator, bg: "bg-tile-blue", fg: "text-osk-blue" },
  { name: "EPI", icon: FlaskConical, bg: "bg-tile-sky", fg: "text-osk-blue" },
  { name: "MAVO", icon: GraduationCap, bg: "bg-tile-blue", fg: "text-osk-navy" },
  { name: "HAVO", icon: GraduationCap, bg: "bg-tile-mint", fg: "text-emerald-600" },
  { name: "VWO", icon: GraduationCap, bg: "bg-tile-red", fg: "text-osk-red" },
  { name: "International School", icon: Globe, bg: "bg-tile-purple", fg: "text-osk-blue" },
];

const SectionHead = ({ title, sub, link, linkLabel, testid }) => (
  <div className="mb-4 flex items-end justify-between gap-4">
    <h2 className="text-xl md:text-[30px]">{title}</h2>
    {sub && <p className="hidden text-sm text-osk-text lg:block">{sub}</p>}
    {link && <Link to={link} className="flex shrink-0 items-center gap-1 text-sm font-bold text-osk-blue hover:underline" data-testid={testid}>{linkLabel} <ArrowRight className="h-4 w-4" /></Link>}
  </div>
);

export const SchoolLevels = () => {
  const { t } = useI18n();
  return (
    <section className="osk-container py-6" data-testid="levels-section">
      <div className="hidden md:block"><SectionHead title={t("levels.title")} sub={t("levels.sub")} /></div>
      <div className="md:hidden"><SectionHead title={t("levels.title")} link="/lessen" linkLabel={t("levels.all")} testid="levels-all-link" /></div>
      <div className="no-scrollbar -mx-5 flex gap-3 overflow-x-auto px-5 pb-2 md:mx-0 md:grid md:grid-cols-7 md:overflow-visible md:px-0">
        {LEVELS.map(({ name, icon: Icon, bg, fg }) => (
          <Link key={name} to={`/lessen?school_type=${encodeURIComponent(name)}`} data-testid={`level-tile-${name}`}
            className={`osk-hover-lift flex w-[84px] shrink-0 flex-col items-center gap-1.5 rounded-2xl ${bg} px-2 py-3 text-center md:w-auto md:flex-row md:justify-between md:px-4 md:py-4 md:text-left`}>
            <Icon className={`h-6 w-6 shrink-0 ${fg}`} strokeWidth={1.8} />
            <span className="text-[13px] font-bold leading-tight text-osk-navy md:flex-1 md:pl-2 md:text-sm">{name}</span>
            <ChevronRight className="hidden h-4 w-4 shrink-0 text-osk-navy md:block" />
          </Link>
        ))}
      </div>
    </section>
  );
};

export const FeaturedLessons = () => {
  const { t } = useI18n();
  const [lessons, setLessons] = useState(null);
  useEffect(() => { api.get("/lessons", { params: { featured: true } }).then((r) => setLessons(r.data)).catch(() => setLessons([])); }, []);
  return (
    <section className="osk-container py-6" data-testid="featured-section">
      <SectionHead title={t("featured.title")} link="/lessen" linkLabel={t("featured.all")} testid="featured-all-link" />
      <div className="no-scrollbar -mx-5 flex snap-x scroll-pl-5 gap-4 overflow-x-auto px-5 pb-3 md:mx-0 md:grid md:grid-cols-2 md:overflow-visible md:px-0 lg:grid-cols-4">
        {(lessons || []).map((l) => <LessonCard key={l.id} lesson={l} className="w-[300px] shrink-0 snap-start md:w-auto" />)}
        {lessons === null && [0, 1, 2, 3].map((i) => <div key={i} className="osk-card h-48 w-[300px] shrink-0 animate-pulse md:w-auto" />)}
      </div>
    </section>
  );
};

const FORMATS = [
  { name: "1-op-1 bijles", icon: User, bg: "bg-tile-blue", fg: "text-osk-blue" },
  { name: "Kleine groep", icon: Users, bg: "bg-tile-yellow", fg: "text-osk-orange" },
  { name: "Groepsles", icon: UsersRound, bg: "bg-tile-sky", fg: "text-osk-blue" },
  { name: "Examenbootcamp", icon: Target, bg: "bg-tile-red", fg: "text-osk-red" },
  { name: "Vakantiecursus", icon: CalendarDays, bg: "bg-tile-mint", fg: "text-emerald-600" },
];

export const Formats = () => {
  const { t } = useI18n();
  return (
    <section className="osk-container py-6" data-testid="formats-section">
      <SectionHead title={t("formats.title")} />
      <div className="no-scrollbar -mx-5 flex gap-4 overflow-x-auto px-5 pb-2 md:mx-0 md:grid md:grid-cols-5 md:overflow-visible md:px-0">
        {FORMATS.map(({ name, icon: Icon, bg, fg }) => (
          <Link key={name} to={`/lessen?format=${encodeURIComponent(name)}`} data-testid={`format-card-${name}`}
            className={`osk-hover-lift w-[230px] shrink-0 rounded-2xl ${bg} p-6 md:w-auto`}>
            <Icon className={`mb-3 h-8 w-8 ${fg}`} strokeWidth={1.8} />
            <p className="text-lg font-extrabold text-osk-navy">{name}</p>
            <p className="mt-1 text-sm text-osk-text">{t(`f.${name}`)}</p>
          </Link>
        ))}
      </div>
    </section>
  );
};

export const TeacherBanner = () => {
  const { t } = useI18n();
  return (
    <section className="osk-container py-6" data-testid="teacher-banner">
      <div className="relative overflow-hidden rounded-[28px] bg-osk-light">
        <div className="grid items-center gap-6 md:grid-cols-[320px_1fr_auto] md:gap-10">
          <div className="relative hidden h-full min-h-[170px] md:block">
            <div className="absolute -left-10 bottom-0 h-28 w-72 rounded-tr-[80px] bg-osk-yellow/90" />
            <PhotoPlaceholder label="Docent foto" className="absolute bottom-0 left-16 h-[160px] w-[200px] rounded-t-3xl" testid="teacher-banner-photo" />
          </div>
          <div className="px-6 pt-6 md:px-0 md:py-8">
            <h2 className="text-2xl md:text-[30px]">{t("banner.title")}</h2>
            <p className="mt-2 max-w-xl text-[15px] text-osk-text">{t("banner.text")}</p>
          </div>
          <div className="px-6 pb-6 md:px-10 md:pb-0">
            <Link to="/docent-worden" className="osk-btn-primary w-full px-8 py-3.5 text-base md:w-auto" data-testid="teacher-banner-btn">{t("banner.btn")} <ArrowRight className="h-5 w-5" /></Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export const Testimonials = () => {
  const [items, setItems] = useState([]);
  const [i, setI] = useState(0);
  useEffect(() => { api.get("/testimonials").then((r) => setItems(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (items.length < 2) return;
    const id = setInterval(() => setI((x) => (x + 1) % items.length), 6000);
    return () => clearInterval(id);
  }, [items.length]);
  if (!items.length) return null;
  const it = items[i];
  return (
    <section className="osk-container py-6" data-testid="testimonials-section">
      <div className="mx-auto flex max-w-4xl flex-col items-center gap-5 md:flex-row">
        {it.photo_file_id ? <img src={photoUrl(it.photo_file_id)} alt={it.author} className="h-20 w-20 rounded-full object-cover" /> : <Avatar size={84} name={it.author} />}
        <div key={it.id} className="osk-rise relative flex-1 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="testimonial-active">
          <Quote className="absolute -top-3 left-5 h-6 w-6 fill-osk-yellow text-osk-yellow" />
          <p className="text-[15px] font-medium text-osk-navy">“{it.quote}”</p>
          <p className="mt-2 text-xs text-osk-muted">— {it.author}{it.role_text ? `, ${it.role_text}` : ""}</p>
        </div>
        <div className="flex gap-2">
          {items.map((x, k) => (
            <button key={x.id} onClick={() => setI(k)} aria-label={`testimonial ${k + 1}`} data-testid={`testimonial-dot-${k}`}
              className={`h-2.5 w-2.5 rounded-full transition-colors ${k === i ? "bg-osk-blue" : "bg-slate-300"}`} />
          ))}
        </div>
      </div>
    </section>
  );
};
